import type { AccessStatus, PeopleSort, PersonRoles } from '../../lib/roles'
import { PRIVILEGED_ROLE_KEYS } from '../../lib/roles'
import { initialsOf } from '../../lib/initials'

/**
 * The four states, plus 042's 'unknown'. These labels are the ONLY place the
 * status becomes English — the value itself is computed in the database (044)
 * and the filter compares against the same string, so a row cannot be labelled
 * Active here and hidden by the Active filter there.
 */
export const STATE_LABEL: Record<AccessStatus, string> = {
  active: 'Active',
  // 'Inactive', not 'Suspended' (David, 27 Sep): nearly every one of these is
  // somebody BambooHR reports Inactive, and the screen should use HR's word.
  // The VALUE stays 'suspended' — it is 054's filter key and the database's.
  suspended: 'Inactive',
  // Two states 045 separated. "No invitation" is the onboarding worklist;
  // "Invited, not used" is the chase list. Before 045 the first read as
  // "Not shown" and the second read as ACTIVE.
  not_invited: 'No invitation',
  invited: 'Invited, not used',
  unverified: 'Unverified',
  unknown: 'Not shown',
}

/**
 * The colour of the status pill (137). Four tones for five states, and the two
 * "not in yet" states share one on purpose: both are the onboarding list, and
 * the words say which half.
 */
export const STATE_TONE: Record<AccessStatus, 'ok' | 'pending' | 'off' | 'muted'> = {
  active: 'ok',
  suspended: 'off',
  not_invited: 'pending',
  invited: 'pending',
  unverified: 'pending',
  unknown: 'muted',
}

/**
 * Coarse on purpose. "3 weeks ago" is what an administrator acts on; the exact
 * minute is in the title attribute for the rare case somebody needs it. A
 * precise running clock on every row is the thing that turns an account screen
 * into a monitoring screen, which is the line 044 deliberately did not cross.
 */
function formatAge(verb: string, iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (days <= 0) return `${verb} today`
  if (days === 1) return `${verb} yesterday`
  if (days < 30) return `${verb} ${days} days ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${verb} ${months} month${months === 1 ? '' : 's'} ago`
  return `${verb} over a year ago`
}

const formatLastSignIn = (iso: string) => formatAge('Signed in', iso)

/**
 * A column header that orders the table.
 *
 * `aria-sort` goes on the TH and the button carries the label, which is the
 * pattern a screen reader announces correctly — a button with aria-sort on
 * itself says nothing, because the sort is a property of the column and not of
 * the control.
 *
 * The arrow is a character and not an icon: the design system's readme permits
 * `×` for dismiss, `↓` for download and `⌕` for search and nothing else, so a
 * new glyph would be a new precedent. `▲`/`▼` are the one case the rule does
 * not reach — they are not an action, they are the current state of the column
 * — and they are marked aria-hidden so the announcement comes from aria-sort.
 */
function SortHeader({
  label,
  column,
  sort,
  sortDesc,
  onSort,
  className,
}: {
  label: string
  column: PeopleSort
  sort: PeopleSort
  sortDesc: boolean
  onSort: (c: PeopleSort) => void
  className?: string
}) {
  const active = sort === column
  return (
    <th
      scope="col"
      className={className}
      aria-sort={active ? (sortDesc ? 'descending' : 'ascending') : 'none'}
    >
      <button type="button" className="admin__sort" onClick={() => onSort(column)}>
        {label}
        <span className="admin__sortmark" aria-hidden="true">
          {active ? (sortDesc ? '▼' : '▲') : ''}
        </span>
      </button>
    </th>
  )
}

/**
 * Everyone, and what they hold.
 *
 * ── ONE ROW PER PERSON, ONE CHIP PER GRANT ──────────────────────────────────
 *
 * The 021 version was a checkbox matrix: a column per role, a tick per cell.
 * Scope (024) breaks that shape — "Comms Administrator for ConeTec Chile" and
 * "Comms Administrator, everywhere" are two grants of one role, and a tick can
 * only say yes or no. So grants are chips, and a chip carries its scope.
 *
 * ── THE PERSON OPENS IN A TRAY (137) ─────────────────────────────────────────
 *
 * The panel used to open as a row inside this table, under the person (that
 * fixed 932 rows putting it off screen). It now opens in a right-hand tray
 * over the list, like the store's items (REC-65), so `onSelect` is all this
 * component knows about it. The NAME is the keyboard control; the rest of the
 * row is a pointer convenience. The select button is its own cell and never
 * opens anything — a row that both selects and opens is how the wrong person
 * ends up in a bulk grant.
 *
 * ── LAST SIGN-IN, NEVER LAST ACTIVE (044) ───────────────────────────────────
 *
 * `auth.users.last_sign_in_at`, rendered coarsely ("3 days ago"), the day on
 * hover and never the time. `auth.sessions.refreshed_at` is one join away and
 * would turn this into a record of when ~900 people last had the hub open.
 *
 * ── THE STATE COMES FROM THE DATABASE (044) ─────────────────────────────────
 *
 * `accessStatus` is computed once server-side and the filter compares against
 * the same string. Do not re-derive it from membershipActive / hasSignedIn.
 */
export function PeopleTable({
  people,
  myPersonId,
  selectedId,
  checked,
  sort,
  sortDesc,
  onSort,
  onSelect,
  onToggleCheck,
  onToggleAll,
  isSelectable = (p) => p.membershipActive,
}: {
  people: PersonRoles[]
  myPersonId: string
  /** The person whose tray is open, if any. */
  selectedId: string | null
  checked: Set<string>
  sort: PeopleSort
  sortDesc: boolean
  onSort: (c: PeopleSort) => void
  onSelect: (id: string) => void
  onToggleCheck: (id: string) => void
  /**
   * Select every selectable row ON THIS PAGE, or clear the selection. Absent,
   * the header has no control. It says how many it means — "all 200 shown" —
   * because 200 of 931 is a page, and "select all" that silently means the
   * page is the 039 mistake in a new costume.
   */
  onToggleAll?: () => void
  /**
   * Whether a row may join a bulk grant: an active membership (a role on a
   * suspended person does nothing) and, for an HR administrator, not somebody
   * who holds a privileged role (137 refuses those).
   */
  isSelectable?: (p: PersonRoles) => boolean
}) {
  const selectable = people.filter(isSelectable)
  const selectedHere = selectable.filter((p) => checked.has(p.personId)).length
  const allState: 'true' | 'false' | 'mixed' =
    selectedHere === 0 ? 'false' : selectedHere === selectable.length ? 'true' : 'mixed'

  return (
    <div className="roles__tablewrap">
      <table className="admin__table people__table">
        <thead>
          <tr>
            <th scope="col" className="admin__checkcol">
              {onToggleAll && selectable.length > 0 ? (
                <button
                  type="button"
                  className="admin__pick"
                  aria-pressed={allState}
                  aria-label={
                    allState === 'false'
                      ? `Select all ${selectable.length} shown`
                      : 'Clear the selection'
                  }
                  title={
                    allState === 'false'
                      ? `Select all ${selectable.length} shown`
                      : 'Clear the selection'
                  }
                  onClick={onToggleAll}
                >
                  {allState === 'mixed' ? '–' : '✓'}
                </button>
              ) : (
                <span className="visually-hidden">Select</span>
              )}
            </th>
            <SortHeader label="Person" column="name" sort={sort} sortDesc={sortDesc} onSort={onSort} />
            {/* BUSINESS UNIT, not "Division" — BambooHR's word. The app says
                business unit everywhere else, and two words for one thing is
                how a screen and a schema drift apart. */}
            <SortHeader
              label="Business unit"
              column="business_unit"
              sort={sort}
              sortDesc={sortDesc}
              onSort={onSort}
            />
            <SortHeader
              label="Department"
              column="department"
              sort={sort}
              sortDesc={sortDesc}
              onSort={onSort}
            />
            <th scope="col">Roles</th>
            <SortHeader
              label="Access"
              column="access"
              sort={sort}
              sortDesc={sortDesc}
              onSort={onSort}
            />
          </tr>
        </thead>
        <tbody>
          {people.map((p) => {
            const isMe = p.personId === myPersonId
            // The employee baseline is on everybody by trigger and says nothing
            // about a person — 900 identical chips hiding the ones that matter.
            const grants = p.grants.filter((g) => g.key !== 'employee')
            const open = selectedId === p.personId
            const canPick = isSelectable(p)

            return (
              <tr
                key={p.personId}
                className={
                  'people__row' +
                  (open ? ' is-selected' : '') +
                  (checked.has(p.personId) ? ' is-checked' : '') +
                  (p.membershipActive ? '' : ' is-suspended')
                }
                onClick={(e) => {
                  // A pointer convenience only. Clicks on a control inside the
                  // row are that control's; the name button is the real one.
                  if ((e.target as HTMLElement).closest('button, a, input, label')) return
                  onSelect(p.personId)
                }}
              >
                <td className="admin__checkcol">
                  {/* A button with aria-pressed, not a checkbox: the native one
                      was 13px on a screen with a 44px floor (046). */}
                  <button
                    type="button"
                    className="admin__pick"
                    aria-pressed={checked.has(p.personId)}
                    aria-label={`Select ${p.displayName}`}
                    disabled={!canPick}
                    title={
                      canPick
                        ? undefined
                        : p.membershipActive
                          ? 'Only a Tenant Administrator can change this person'
                          : 'Inactive — nothing can be granted'
                    }
                    onClick={() => onToggleCheck(p.personId)}
                  >
                    ✓
                  </button>
                </td>

                <th scope="row" className="roles__person">
                  <div className="roles__personin">
                  <span className="pavatar" aria-hidden="true">
                    {initialsOf(p.displayName)}
                  </span>
                  <span className="roles__who">
                    <button
                      type="button"
                      className="roles__open"
                      aria-haspopup="dialog"
                      aria-expanded={open}
                      onClick={() => onSelect(p.personId)}
                    >
                      <span className="roles__name">{p.displayName}</span>
                      {isMe && <span className="roles__flag roles__flag--you">you</span>}
                    </button>
                    <span className="roles__meta">
                      {/* The job title, because "Field Engineer" is what tells
                          an admin they have the right Robert. */}
                      {p.jobTitle ?? p.locale}
                    </span>
                  </span>
                  </div>
                </th>

                <td className="roles__org">
                  {p.businessUnitName ? (
                    <>
                      <span className="roles__orgname">{p.businessUnitName}</span>
                      {/* The entity underneath: a BU name is only unique
                          inside one, and rule 1's hierarchy is entity → BU. */}
                      {p.legalEntityName && <span className="roles__meta">{p.legalEntityName}</span>}
                    </>
                  ) : (
                    // A worklist (hr_unmapped's no_division_mapping), not a gap.
                    <span className="admin__muted">Unassigned</span>
                  )}
                </td>

                <td className="roles__org">
                  {p.departmentName ?? <span className="admin__muted">—</span>}
                </td>

                <td>
                  {grants.length === 0 ? (
                    <span className="admin__muted">Employee only</span>
                  ) : (
                    <ul className="admin__chips">
                      {grants.map((g) => (
                        <li
                          key={`${g.key}:${g.scopeId ?? 'all'}`}
                          className={
                            'rolechip' +
                            (g.scopeId ? ' chip--scoped' : '') +
                            (PRIVILEGED_ROLE_KEYS.includes(g.key) ? ' rolechip--priv' : '')
                          }
                        >
                          {g.name}
                          {/* The scope is the point. A chip without it would
                              claim tenant-wide power the person does not have. */}
                          {g.scopeName && <span className="chip__scope">{g.scopeName}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>

                <td>
                  <span className={`statuspill statuspill--${STATE_TONE[p.accessStatus]}`}>
                    {STATE_LABEL[p.accessStatus]}
                  </span>
                  {/* "Active" answers "can they get in"; the date answers "is
                      this account dormant". Absent rather than "—" for somebody
                      who has never signed in — the state already says so. */}
                  {p.lastSignInAt ? (
                    <span className="admin__substate" title={new Date(p.lastSignInAt).toLocaleDateString()}>
                      {formatLastSignIn(p.lastSignInAt)}
                    </span>
                  ) : (
                    p.invitedAt && (
                      <span className="admin__substate" title={new Date(p.invitedAt).toLocaleDateString()}>
                        {formatAge('Invited', p.invitedAt)}
                      </span>
                    )
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
